// Custom hook for app installation detection

import { useState, useEffect, useCallback } from 'react';
import {
  isMobileWeb,
  isAndroidDevice,
  isIOSDevice,
  checkIfAppInstalled,
  getPlatformType,
  clearAppInstallCache,
} from 'shared/components/AppMigration/helpers';
import { IAppDetectionState } from 'shared/components/AppMigration/types';

interface IUseAppInstallDetectionOptions {
  /**
   * Whether to skip detection (e.g., on server-side)
   */
  skip?: boolean;
}

interface IUseAppInstallDetectionReturn extends IAppDetectionState {
  /**
   * Re-check app installation (clears cache and re-detects)
   */
  recheck: () => Promise<void>;
  /**
   * Whether the device is mobile web (not in-app)
   */
  isMobile: boolean;
}

/**
 * Custom hook to detect if the Nykaa Fashion app is installed
 *
 * On Android: Uses getInstalledRelatedApps API (Chrome 80+)
 * On iOS: Cannot reliably detect, always returns null (shows "Use App")
 *
 * @param options - Configuration options
 * @returns App detection state and utility functions
 */
export const useAppInstallDetection = (
  options: IUseAppInstallDetectionOptions = {}
): IUseAppInstallDetectionReturn => {
  const { skip = false } = options;

  const [state, setState] = useState<IAppDetectionState>({
    isLoading: true,
    isAppInstalled: null,
    platform: 'unknown',
  });

  const [isMobile, setIsMobile] = useState(false);

  const detectAppInstallation = useCallback(async () => {
    // Skip on server-side or if explicitly disabled
    if (typeof window === 'undefined' || skip) {
      setState({
        isLoading: false,
        isAppInstalled: null,
        platform: 'unknown',
      });
      return;
    }

    // Check if we're on mobile web
    const mobile = isMobileWeb();
    setIsMobile(mobile);

    if (!mobile) {
      setState({
        isLoading: false,
        isAppInstalled: null,
        platform: 'unknown',
      });
      return;
    }

    // Determine platform
    const platform = getPlatformType();

    // For Android: Attempt to detect app installation
    if (isAndroidDevice()) {
      try {
        const isInstalled = await checkIfAppInstalled();
        setState({
          isLoading: false,
          isAppInstalled: isInstalled,
          platform: 'android',
        });
      } catch {
        setState({
          isLoading: false,
          isAppInstalled: false,
          platform: 'android',
        });
      }
      return;
    }

    // For iOS: Cannot reliably detect, return null
    // AppsFlyer will handle the smart redirect
    if (isIOSDevice()) {
      setState({
        isLoading: false,
        isAppInstalled: null, // null means "unknown" - show "Use App"
        platform: 'ios',
      });
      return;
    }

    // Unknown platform
    setState({
      isLoading: false,
      isAppInstalled: null,
      platform,
    });
  }, [skip]);

  // Initial detection on mount
  useEffect(() => {
    detectAppInstallation();
  }, [detectAppInstallation]);

  // Re-check function that clears cache first
  const recheck = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true }));
    clearAppInstallCache();
    await detectAppInstallation();
  }, [detectAppInstallation]);

  return {
    ...state,
    recheck,
    isMobile,
  };
};

export default useAppInstallDetection;
